// SpecialQuestionService — CRUD for Sonderfragen (special questions / props).
//
// Follows the same REST + singleton pattern as TipRoundService:
// anon headers for reads, authed headers (JWT from localStorage) for writes.
//
// Three tables:
//   special_questions        — question definitions (admin creates, cron auto-generates)
//   special_question_options — answer options per question
//   special_question_answers — user answers (RESTRICTIVE deadline enforced by DB)
//
// Point booking happens automatically via a PostgreSQL trigger when a question's
// status is set to 'resolved' — no extra client call needed.

import { isSpecialQuestionLocked } from "../utils/tipVisibility.ts";
import type {
  SpecialQuestion,
  SpecialQuestionAnswer,
  SpecialQuestionOption,
} from "../types/index.ts";

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";

const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_KEY = `sb-${PROJECT_REF}-auth-token`;

const getToken = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.access_token ?? p?.currentSession?.access_token ?? null;
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${getToken() ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
  Prefer: "return=representation",
});

// ---------------------------------------------------------------------------
// Types for admin creation
// ---------------------------------------------------------------------------

export interface CreateQuestionInput {
  tipRoundId: number;
  leagueId: number;
  scope: "preseason" | "block";
  questionText: string;
  pointsAward: number;
  answerDeadline: string; // ISO timestamp
  options: Array<{ text: string; key: string }>;
  /**
   * 'free_text' questions start without options — they are created from the
   * submitted answers when the admin resolves them (Migration 049).
   */
  answerFormat?: "options" | "free_text";
  /** Defaults to 'manual' when omitted. */
  resolutionMode?: "manual" | "auto";
  /** Resolver key from _shared/questions.ts (only used when resolutionMode='auto'). */
  autoResolverKey?: string;
  /** Resolver parameters (e.g. { n: 20 } for first_team_to_n_goals). */
  resolverParams?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class SpecialQuestionService {
  private static instance: SpecialQuestionService;
  static getInstance(): SpecialQuestionService {
    if (!this.instance) this.instance = new SpecialQuestionService();
    return this.instance;
  }
  private constructor() {}

  /**
   * Fetch all questions for a round + league, enriched with options and the
   * current user's answer. Returns questions sorted by opens_at DESC.
   */
  async listForRound(
    tipRoundId: number,
    leagueId: number,
    userId: string,
  ): Promise<SpecialQuestion[]> {
    // 1. Fetch questions — requires authenticated JWT (RLS: auth.role() = 'authenticated')
    const qRes = await fetch(
      `${SUPABASE_URL}/rest/v1/special_questions` +
        `?tip_round_id=eq.${tipRoundId}` +
        `&league_id=eq.${leagueId}` +
        `&order=opens_at.desc`,
      { headers: authedHeaders() },
    );
    if (!qRes.ok) return [];
    const questions: SpecialQuestion[] = await qRes.json();
    if (questions.length === 0) return [];

    const ids = questions.map((q) => q.id).join(",");

    // 2. Fetch all options for these questions
    const oRes = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_options` +
        `?question_id=in.(${ids})` +
        `&order=display_order.asc`,
      { headers: authedHeaders() },
    );
    const options: SpecialQuestionOption[] = oRes.ok ? await oRes.json() : [];

    // 3. Fetch current user's answers
    const aRes = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_answers` +
        `?question_id=in.(${ids})` +
        `&user_id=eq.${userId}`,
      { headers: authedHeaders() },
    );
    const answers: SpecialQuestionAnswer[] = aRes.ok ? await aRes.json() : [];

    // 4. Join client-side
    const optsByQ = new Map<number, SpecialQuestionOption[]>();
    for (const o of options) {
      const arr = optsByQ.get(o.question_id) ?? [];
      arr.push(o);
      optsByQ.set(o.question_id, arr);
    }
    const answerByQ = new Map(answers.map((a) => [a.question_id, a]));

    return questions.map((q) => ({
      ...q,
      options: optsByQ.get(q.id) ?? [],
      myAnswer: answerByQ.get(q.id) ?? null,
    }));
  }

  /**
   * Upsert the current user's answer for a question.
   * DB enforces the deadline via RESTRICTIVE RLS — returns false if too late.
   */
  async submitAnswer(
    questionId: number,
    optionId: number,
    userId: string,
  ): Promise<boolean> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_answers`,
      {
        method: "POST",
        headers: {
          ...authedHeaders(),
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({
          question_id: questionId,
          user_id: userId,
          option_id: optionId,
          answered_at: new Date().toISOString(),
        }),
      },
    );
    return res.ok || res.status === 204 || res.status === 200;
  }

  /**
   * Upsert the current user's free-text answer.
   * Same deadline enforcement as submitAnswer — the RESTRICTIVE RLS policy
   * applies to the row regardless of which column carries the answer.
   */
  async submitTextAnswer(
    questionId: number,
    answerText: string,
    userId: string,
  ): Promise<boolean> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_answers`,
      {
        method: "POST",
        headers: {
          ...authedHeaders(),
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({
          question_id: questionId,
          user_id: userId,
          answer_text: answerText.trim(),
          option_id: null,
          answered_at: new Date().toISOString(),
        }),
      },
    );
    return res.ok || res.status === 204 || res.status === 200;
  }

  /**
   * Admin: all free-text answers for a question, with display names.
   * Backed by a SECURITY DEFINER function — the regular policies deliberately
   * don't expose other users' answers (Migration 049).
   */
  async listFreeTextAnswers(
    questionId: number,
  ): Promise<Array<{ answer_id: number; display_name: string | null; answer_text: string }>> {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/list_free_text_answers`, {
      method: "POST",
      headers: authedHeaders(),
      body: JSON.stringify({ p_question_id: questionId }),
    });
    if (!res.ok) return [];
    return await res.json();
  }

  /**
   * Admin: turn grouped free-text answers into options and resolve.
   *
   * Passing correctKey = null only stores the grouping, which lets the admin
   * review it before committing to a winner. The DB function handles the
   * owner check and awards points via the existing trigger.
   */
  async resolveFreeTextQuestion(
    questionId: number,
    groups: Array<{ key: string; label: string; answerIds: number[] }>,
    correctKey: string | null,
  ): Promise<boolean> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/resolve_free_text_question`,
      {
        method: "POST",
        headers: authedHeaders(),
        body: JSON.stringify({
          p_question_id: questionId,
          p_groups: groups.map((g) => ({
            key: g.key,
            label: g.label,
            answer_ids: g.answerIds,
          })),
          p_correct_key: correctKey,
        }),
      },
    );
    return res.ok || res.status === 204;
  }

  /**
   * Admin: create a manual question with its options.
   * Returns the new question's id on success, null on failure.
   */
  async createQuestion(input: CreateQuestionInput): Promise<number | null> {
    // Insert the question
    const qRes = await fetch(`${SUPABASE_URL}/rest/v1/special_questions`, {
      method: "POST",
      headers: authedHeaders(),
      body: JSON.stringify({
        tip_round_id: input.tipRoundId,
        league_id: input.leagueId,
        scope: input.scope,
        question_text: input.questionText,
        points_award: input.pointsAward,
        answer_deadline: input.answerDeadline,
        resolution_mode: input.resolutionMode ?? "manual",
        auto_resolver_key: input.autoResolverKey ?? null,
        resolver_params: input.resolverParams ?? null,
        answer_format: input.answerFormat ?? "options",
        status: "open",
      }),
    });
    if (!qRes.ok) return null;
    const created = await qRes.json();
    const questionId: number = Array.isArray(created)
      ? created[0]?.id
      : created?.id;
    if (!questionId) return null;

    // Free-text questions have no options up front — they are created from the
    // answers at resolution time.
    if (input.answerFormat === "free_text") return questionId;

    // Insert options
    const optRows = input.options.map((o, i) => ({
      question_id: questionId,
      option_text: o.text,
      option_key: o.key.toLowerCase(),
      display_order: i,
    }));
    const oRes = await fetch(`${SUPABASE_URL}/rest/v1/special_question_options`, {
      method: "POST",
      headers: { ...authedHeaders(), Prefer: "return=minimal" },
      body: JSON.stringify(optRows),
    });
    return oRes.ok ? questionId : null;
  }

  /**
   * Admin: resolve a manual question by selecting the correct option.
   * The DB trigger automatically awards points to all answerers.
   */
  async resolveManually(
    questionId: number,
    correctOptionId: number,
  ): Promise<boolean> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/special_questions?id=eq.${questionId}`,
      {
        method: "PATCH",
        headers: { ...authedHeaders(), Prefer: "return=minimal" },
        body: JSON.stringify({
          status: "resolved",
          correct_option_id: correctOptionId,
          resolved_at: new Date().toISOString(),
        }),
      },
    );
    return res.ok || res.status === 204;
  }

  /**
   * Admin: delete an open question. Options and answers cascade automatically.
   */
  async deleteQuestion(questionId: number): Promise<boolean> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/special_questions?id=eq.${questionId}`,
      {
        method: "DELETE",
        headers: { ...authedHeaders(), Prefer: "return=minimal" },
      },
    );
    return res.ok || res.status === 204;
  }

  /**
   * Admin: fetch all questions for a round (all leagues, all statuses).
   * Used by the admin panel.
   */
  async listAllForRound(tipRoundId: number): Promise<SpecialQuestion[]> {
    const qRes = await fetch(
      `${SUPABASE_URL}/rest/v1/special_questions` +
        `?tip_round_id=eq.${tipRoundId}` +
        `&order=opens_at.asc`,
      { headers: authedHeaders() },
    );
    if (!qRes.ok) return [];
    const questions: SpecialQuestion[] = await qRes.json();
    if (questions.length === 0) return [];

    const ids = questions.map((q) => q.id).join(",");
    const oRes = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_options` +
        `?question_id=in.(${ids})&order=display_order.asc`,
      { headers: authedHeaders() },
    );
    const options: SpecialQuestionOption[] = oRes.ok ? await oRes.json() : [];

    const optsByQ = new Map<number, SpecialQuestionOption[]>();
    for (const o of options) {
      const arr = optsByQ.get(o.question_id) ?? [];
      arr.push(o);
      optsByQ.set(o.question_id, arr);
    }
    return questions.map((q) => ({ ...q, options: optsByQ.get(q.id) ?? [] }));
  }

  /**
   * Get the count of correct answers for a resolved question (for the results summary).
   */
  async getResultSummary(
    questionId: number,
  ): Promise<{ correct: number; total: number } | null> {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/special_question_answers` +
        `?question_id=eq.${questionId}&select=awarded_points`,
      { headers: authedHeaders() },
    );
    if (!res.ok) return null;
    const rows: Array<{ awarded_points: number | null }> = await res.json();
    const total = rows.length;
    const correct = rows.filter((r) => (r.awarded_points ?? 0) > 0).length;
    return { correct, total };
  }

  /**
   * Fragen einer Runde samt der Antworten ALLER Spieler — aber nur zu Fragen,
   * deren Antwortfrist abgelaufen ist. Für die Tipp-Vorschau.
   *
   * Die RLS-Policy sqa_select_authenticated lässt jeden Angemeldeten jede
   * Antwort lesen, auch die zu noch laufenden Fragen. Deshalb wird hier schon
   * beim Abruf gefiltert: Antworten auf offene Fragen gelangen gar nicht erst
   * in den Browser. Die vollständige Fragenliste kommt trotzdem zurück — der
   * Aufrufer muss wissen, wie viele Fragen noch laufen, um es anzuzeigen.
   *
   * Unterscheidet sich bewusst von listForRound (nur eigene Antworten) und
   * getAwardedPointsByUser (nur Summen, keine Antworttexte).
   */
  async listLockedAnswersForRound(
    tipRoundId: number,
    leagueId: number,
    now: number = Date.now(),
  ): Promise<{
    questions: SpecialQuestion[];
    options: SpecialQuestionOption[];
    answers: SpecialQuestionAnswer[];
    /** Die Fragen, deren Antworten tatsächlich geholt wurden. */
    lockedQuestionIds: number[];
  }> {
    const empty = { questions: [], options: [], answers: [], lockedQuestionIds: [] };
    try {
      const qRes = await fetch(
        `${SUPABASE_URL}/rest/v1/special_questions` +
          `?tip_round_id=eq.${tipRoundId}` +
          `&or=(league_id.is.null,league_id.eq.${leagueId})` +
          `&order=answer_deadline.asc`,
        { headers: authedHeaders() },
      );
      if (!qRes.ok) return empty;
      const questions = (await qRes.json()) as SpecialQuestion[];
      if (questions.length === 0) return empty;

      const lockedIds = questions
        .filter((q) => isSpecialQuestionLocked(q, now))
        .map((q) => q.id);
      // Keine abgelaufene Frage → gar nichts abzuholen.
      if (lockedIds.length === 0)
        return { questions, options: [], answers: [], lockedQuestionIds: [] };

      const idFilter = lockedIds.join(",");
      const [oRes, aRes] = await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/special_question_options` +
            `?question_id=in.(${idFilter})&order=display_order.asc`,
          { headers: authedHeaders() },
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/special_question_answers` +
            `?question_id=in.(${idFilter})&select=*`,
          { headers: authedHeaders() },
        ),
      ]);

      return {
        questions,
        options: oRes.ok ? ((await oRes.json()) as SpecialQuestionOption[]) : [],
        answers: aRes.ok ? ((await aRes.json()) as SpecialQuestionAnswer[]) : [],
        lockedQuestionIds: lockedIds,
      };
    } catch (e) {
      console.error("SpecialQuestionService.listLockedAnswersForRound failed:", e);
      return empty;
    }
  }

  /**
   * Sum awarded special-question points per user for a given round.
   *
   * This is the "Sonderfragen"/bonus scoring channel — kept strictly separate
   * from the table/tournament ("Kern") scoring. It is never added into the
   * canonical ranking; callers decide, purely for display, whether/how to
   * combine it with the core score.
   */
  async getAwardedPointsByUser(
    tipRoundId: number,
  ): Promise<Record<string, number>> {
    try {
      // Step 1: get all question IDs for this round
      const qRes = await fetch(
        `${SUPABASE_URL}/rest/v1/special_questions?tip_round_id=eq.${tipRoundId}&select=id`,
        { headers: authedHeaders() },
      );
      if (!qRes.ok) return {};
      const questions = (await qRes.json()) as Array<{ id: number }>;
      if (questions.length === 0) return {};

      const qIds = questions.map((q) => q.id).join(",");

      // Step 2: get answers with awarded_points for those questions
      const aRes = await fetch(
        `${SUPABASE_URL}/rest/v1/special_question_answers` +
          `?question_id=in.(${qIds})` +
          `&awarded_points=not.is.null` +
          `&select=user_id,awarded_points`,
        { headers: authedHeaders() },
      );
      if (!aRes.ok) return {};
      const answers = (await aRes.json()) as Array<{
        user_id: string;
        awarded_points: number;
      }>;

      const bonusByUser: Record<string, number> = {};
      for (const a of answers) {
        bonusByUser[a.user_id] =
          (bonusByUser[a.user_id] ?? 0) + Number(a.awarded_points);
      }
      return bonusByUser;
    } catch {
      return {};
    }
  }
}

export const specialQuestionService = SpecialQuestionService.getInstance();
